import { Site, formatNumber } from '@debbie/common/dist/index.js';
import { objectBy } from '@debbie/common/dist/util/index.js';
import { TRIGGER_AGENT_PROMPT_MAX_LENGTH, TodoType, } from '@debbie/common/dist/workflows/index.js';
import { useCaseGroups } from '@debbie/cortex/dist/case-groups/index.js';
import { useCollector } from '@debbie/cortex/dist/collectors/index.js';
import { useConnectConnectionsFrom } from '@debbie/cortex/dist/connect/index.js';
import { useEndReasons } from '@debbie/cortex/dist/end-reasons/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { useTeams } from '@debbie/cortex/dist/teams/index.js';
import { useTemplatesMap } from '@debbie/cortex/dist/templates/index.js';
import { useUpdateTypesMap } from '@debbie/cortex/dist/update-types/index.js';
import { useVoucherTypes } from '@debbie/cortex/dist/voucher-types/index.js';
import { useWorkflows } from '@debbie/cortex/dist/workflows/index.js';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { Size } from '../../design-system.js';
import THEME from '../../theme.js';
import { toLink } from '../../utils/index.js';
import Button from '../Button.js';
import Checkbox from '../Checkbox.js';
import DraggableList from '../DraggableList.js';
import ExternalLink from '../Icons/ExternalLink.js';
import { Edit, File, Info } from '../Icons/index.js';
import Trash2 from '../Icons/Trash2.js';
import Modal from '../Modal.js';
import UserPreview from '../performed-by/UserPreview.js';
import TeamPreview from '../TeamPreview.js';
import { TemplateEditorTabSet } from '../templates/index.js';
import Textarea from '../Textarea.js';
const Procedure = (props) => {
    const [editableProcedure, setEditableProcedure] = useState(props.procedure);
    useEffect(() => {
        setEditableProcedure(props.procedure);
    }, [props.procedure]);
    if (!editableProcedure) {
        return null;
    }
    return (React.createElement(ProcedureBuilderStyled, null, props.onChange ? (React.createElement(DraggableList, { list: editableProcedure.map((todo, i) => ({
            id: todo.id,
            content: (React.createElement(TodoComponent, { i: i, todo: todo, onChange: props.onChange, procedure: props.procedure })),
        })), onChange: (items) => {
            if (props.onChange) {
                const newProcedure = items.map((item) => {
                    const todo = editableProcedure.find((todo) => todo.id === item.id);
                    if (!todo) {
                        throw new Error('todo not found in procedure onChange');
                    }
                    return todo;
                });
                setEditableProcedure(newProcedure);
                props.onChange(newProcedure);
            }
        } })) : (editableProcedure.map((todo, i) => (React.createElement(TodoComponent, { key: todo.id, i: i, todo: todo, onChange: props.onChange, procedure: props.procedure }))))));
};
export default Procedure;
const ProcedureBuilderStyled = styled.div `
  text-align: left;

  & > h3 {
    font-size: 14px;
    margin-top: 0.5em;
  }
`;
const TodoNumber = styled.span `
  background-color: ${THEME.COLOR_ACCENT};
  color: ${THEME.COLOR_SECONDARY};
  width: 10px;
  height: 10px;
  padding: 2px 8px;
  border-radius: 3px;
  margin-right: 5px;
`;
const TodoStyled = styled.div `
  font-size: 12px;
  background: ${THEME.FILL};
  padding: 5px 10px 5px 3px;
  border-radius: 3px;
  & > svg {
    float: right;
  }

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;
const TodoComponent = (props) => {
    const { todo, i } = props;
    const lang = useSelector((s) => s.lang);
    const site = useSelector((s) => s.site);
    const templatesMapLoadable = useTemplatesMap();
    const templatesMap = h.hasData(templatesMapLoadable)
        ? templatesMapLoadable.data
        : {};
    const collectorLoadable = useCollector(todo.type === TodoType.SetCollector ? todo.details.collectorId : undefined);
    const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null;
    const caseGroupsLoadable = useCaseGroups();
    const caseGroups = h.hasData(caseGroupsLoadable)
        ? caseGroupsLoadable.data
        : null;
    const voucherTypesLoadable = useVoucherTypes();
    const voucherTypes = h.hasData(voucherTypesLoadable)
        ? voucherTypesLoadable.data
        : null;
    const workflowsLoadable = useWorkflows();
    const workflows = h.hasData(workflowsLoadable) ? workflowsLoadable.data : null;
    const endReasons = useEndReasons();
    const endReasonLookup = h.hasData(endReasons)
        ? objectBy(endReasons.data, (er) => er.id)
        : {};
    const fromConnections = useConnectConnectionsFrom();
    const connectionLookup = objectBy(h.hasData(fromConnections) ? fromConnections.data : [], (c) => c.id);
    const workflowsLookup = objectBy(workflows ? workflows : [], (workflow) => workflow.workflowId);
    const updateTypesMapLoadable = useUpdateTypesMap();
    const updateTypesMap = h.hasData(updateTypesMapLoadable)
        ? updateTypesMapLoadable.data
        : {};
    const [editTemplateId, setEditTemplateId] = useState(null);
    const [showTodoDetails, setShowTodoDetails] = useState(false);
    const teamsLoadable = useTeams();
    const teams = h.hasData(teamsLoadable) ? teamsLoadable.data : null;
    return (React.createElement("div", { style: { paddingTop: '5px' } },
        React.createElement(TodoStyled, null,
            React.createElement(TodoNumber, null, i + 1),
            ' ',
            (() => {
                switch (todo.type) {
                    case TodoType.AddVoucher: {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.economyVoucherTypeLabels[todo.details.voucherTypeId] ||
                                voucherTypes?.find((vt) => vt.voucherTypeId === todo.details.voucherTypeId)?.label),
                            todo.details.amount
                                ? ` ${todo.details.currency} ${formatNumber(todo.details.amount)}`
                                : '',
                            todo.details.maxTotalAmount ? (React.createElement("span", { style: { fontSize: '0.85em' } },
                                ' ',
                                '<',
                                " ",
                                formatNumber(todo.details.maxTotalAmount))) : ('')));
                    }
                    case TodoType.AddBillingVoucher: {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.economyVoucherTypeLabels[todo.details.voucherTypeId] ||
                                voucherTypes?.find((vt) => vt.voucherTypeId === todo.details.voucherTypeId)?.label),
                            todo.details.amount
                                ? ` ${todo.details.currency} ${formatNumber(todo.details.amount)}`
                                : '',
                            ' ',
                            todo.details.text));
                    }
                    case TodoType.RemoveVoucher: {
                        return (React.createElement(React.Fragment, null,
                            lang.workflows.removeVouchers,
                            ' ',
                            React.createElement("b", null, lang.pluralEconomyVoucherTypeLabels[todo.details.voucherTypeId] ||
                                voucherTypes?.find((vt) => vt.voucherTypeId === todo.details.voucherTypeId)?.label)));
                    }
                    case TodoType.CreateInteraction: {
                        const template = todo.details.templateId &&
                            templatesMap?.[todo.details.templateId];
                        return (React.createElement(React.Fragment, null,
                            editTemplateId && (React.createElement(Modal, { header: ' ', onToggle: () => setEditTemplateId(null), maxWidth: "1400px" },
                                React.createElement(TemplateEditorTabSet, { templateId: editTemplateId, preventDelete: true }))),
                            React.createElement("b", null,
                                lang.interactionTypes[todo.details.type],
                                ' ',
                                Site.Caseworker === site &&
                                    (props.onChange ? (React.createElement(Edit, { style: {
                                            cursor: 'pointer',
                                        }, onClick: () => setEditTemplateId(todo.details.templateId) })) : (React.createElement(ExternalLink, { style: {
                                            cursor: 'pointer',
                                        }, onClick: () => c.redirect.redirect(`/settings/template-editor/${todo.details.templateId}`) })))),
                            ' ',
                            template && template.name,
                            ' ',
                            React.createElement("span", null,
                                todo.details.includeVoucherAttachments && React.createElement(File, null),
                                (todo.details.attachments || []).map((a) => {
                                    const template = a.templateId && templatesMap?.[a.templateId];
                                    return (React.createElement(React.Fragment, { key: a.templateId },
                                        React.createElement("span", { title: template
                                                ? `${template.name} ${template.fileName
                                                    ? ` (${template.fileName})`
                                                    : ''}`
                                                : '' },
                                            React.createElement(File, null))));
                                })),
                            React.createElement(Button, { theme: "text", onClick: () => {
                                    setShowTodoDetails(true);
                                } },
                                React.createElement(Info, null))));
                    }
                    case TodoType.CreateCreditorInteraction: {
                        const creditorTemplate = todo.details.templateId && templatesMap[todo.details.templateId];
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null,
                                lang.interactionTypes[todo.details.type],
                                ' ',
                                lang.workflows.creditorInteraction,
                                ' ',
                                Site.Caseworker === site && (React.createElement(ExternalLink, { style: {
                                        cursor: 'pointer',
                                    }, onClick: () => c.redirect.redirect(`/settings/template-editor/${todo.details.templateId}`) }))),
                            ' ',
                            creditorTemplate && creditorTemplate.name,
                            ' ',
                            React.createElement("span", null,
                                todo.details.includeVoucherAttachments && React.createElement(File, null),
                                (todo.details.attachments || []).map((a) => {
                                    const template = a.templateId && templatesMap[a.templateId];
                                    return (React.createElement(React.Fragment, { key: a.templateId },
                                        React.createElement("span", { title: template
                                                ? `${template.name} ${template.fileName
                                                    ? ` (${template.fileName})`
                                                    : ''}`
                                                : '' },
                                            React.createElement(File, null)),
                                        React.createElement(Button, { theme: "text", onClick: () => {
                                                setShowTodoDetails(true);
                                            } },
                                            React.createElement(Info, null))));
                                }))));
                    }
                    case TodoType.CreateOtherInteraction: {
                        const otherTemplate = todo.details.templateId && templatesMap[todo.details.templateId];
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null,
                                lang.interactionTypes[todo.details.type],
                                " -",
                                ' ',
                                todo.details.to,
                                ' ',
                                Site.Caseworker === site && (React.createElement(ExternalLink, { style: {
                                        cursor: 'pointer',
                                    }, onClick: () => c.redirect.redirect(`/settings/template-editor/${todo.details.templateId}`) }))),
                            ' ',
                            otherTemplate && otherTemplate.name,
                            ' ',
                            React.createElement("span", null,
                                todo.details.includeVoucherAttachments && React.createElement(File, null),
                                (todo.details.attachments || []).map((a) => {
                                    const template = a.templateId && templatesMap[a.templateId];
                                    return (React.createElement(React.Fragment, { key: a.templateId },
                                        React.createElement("span", { title: template
                                                ? `${template.name} ${template.fileName
                                                    ? ` (${template.fileName})`
                                                    : ''}`
                                                : '' },
                                            React.createElement(File, null)),
                                        React.createElement(Button, { theme: "text", onClick: () => {
                                                setShowTodoDetails(true);
                                            } },
                                            React.createElement(Info, null))));
                                }))));
                    }
                    case TodoType.SetCollector: {
                        return (React.createElement(React.Fragment, null, todo.details.useCreditor ? (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.workflowTodoTypes[todo.type]),
                            ' ',
                            lang.workflows.creditorCollector,
                            ' ',
                            todo.details.useCreditor)) : (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.workflowTodoTypes[todo.type]),
                            ' ',
                            collector && collector.name,
                            " ",
                            todo.details.useCreditor))));
                    }
                    case TodoType.SetCaseGroup: {
                        const caseGroup = todo.details.caseGroupId &&
                            caseGroups?.find((cg) => cg.id === todo.details.caseGroupId);
                        return React.createElement(React.Fragment, null, caseGroup && caseGroup.label);
                    }
                    case TodoType.ConnectToDebtCollector: {
                        const connection = connectionLookup[todo.details.connectionId];
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.workflowTodoTypes[todo.type]),
                            React.createElement("div", { style: {
                                    background: '#ffffff',
                                    padding: '10px',
                                    borderRadius: '3px',
                                    margin: '5px -7px -2px 0',
                                } },
                                React.createElement("div", null, connection?.product.logo != null ? (React.createElement(React.Fragment, null,
                                    React.createElement("img", { src: toLink(connection.product.logo.fileId, connection.product.logo.fileName ?? ''), style: {
                                            maxWidth: '100%',
                                            height: 'auto',
                                            maxHeight: '48px',
                                            marginBottom: '0.5rem',
                                        } }),
                                    React.createElement("p", { style: { margin: 0 } }, connection.product.name))) : null))));
                    }
                    case TodoType.ChangeCaseStatus: {
                        const endReason = todo.details.endReasonId
                            ? endReasonLookup[todo.details.endReasonId]
                            : null;
                        return (React.createElement("span", { title: `${lang.cases[todo.details.status]} - ${endReason
                                ? (lang.endReason[endReason.handle] ??
                                    endReason.label)
                                : ''}` },
                            React.createElement("b", null, lang.workflowTodoTypes[todo.type]),
                            ' ',
                            lang.cases[todo.details.status],
                            " -",
                            ' ',
                            endReason
                                ? (lang.endReason[endReason.handle] ??
                                    endReason.label)
                                : ''));
                    }
                    case TodoType.SetCaseTag: {
                        return (React.createElement("b", null,
                            lang.workflowTodoTypes[todo.type],
                            " -",
                            ' ',
                            lang.caseTags[todo.details.tag] ||
                                todo.details.tag));
                    }
                    case TodoType.RemoveCaseTag: {
                        return (React.createElement("b", null,
                            lang.workflowTodoTypes[todo.type],
                            " -",
                            ' ',
                            lang.caseTags[todo.details.tag] ||
                                todo.details.tag));
                    }
                    case TodoType.SwitchWorkflow: {
                        return (React.createElement("span", { onClick: () => {
                                c.redirect.redirect(`/workflows/${todo.details.toWorkflowId}`);
                            }, style: { cursor: 'pointer' } },
                            React.createElement("b", null, lang.workflowTodoTypes[todo.type]),
                            " -",
                            ' ',
                            React.createElement("span", { style: { textDecoration: 'underline' } }, workflowsLookup[todo.details.toWorkflowId]?.title ||
                                todo.details.toWorkflowId)));
                    }
                    case TodoType.SwitchBackToPreviousWorkflow: {
                        return React.createElement("b", null, lang.workflowTodoTypes[todo.type]);
                    }
                    case TodoType.MergeCase: {
                        return (React.createElement("b", { title: (todo.details.mergeWithRelatedCases || [])
                                .map((relationType) => lang.customerCaseRelationType[relationType])
                                .join(', ') },
                            lang.workflowTodoTypes[todo.type],
                            ' ',
                            React.createElement(Button, { theme: "text", onClick: () => {
                                    setShowTodoDetails(true);
                                } },
                                React.createElement(Info, null))));
                    }
                    case TodoType.CreateProperties: {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.workflowTodoTypes[todo.type]),
                            ' ',
                            React.createElement("span", null, todo.details.propertyRef === 'case-voucher'
                                ? lang.properties.caseVouchers
                                : lang.properties.cases)));
                    }
                    case TodoType.SetResponsible: {
                        const team = teams?.find((t) => t.id === todo.details?.referenceId);
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.workflowTodoTypes[todo.type]),
                            ' ',
                            React.createElement("div", { style: {
                                    margin: '-15px 0px -15px 5px',
                                    display: 'inline-block',
                                } },
                                todo.details?.referenceId &&
                                    todo.details.type === 'user' && (React.createElement(UserPreview, { id: todo.details.referenceId })),
                                todo.details?.referenceId &&
                                    todo.details.type === 'team' &&
                                    team && React.createElement(TeamPreview, { team: team }))));
                    }
                    case TodoType.SetCaseSetting: {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.workflowTodoTypes[todo.type]),
                            ' ',
                            lang.cases.caseSettings[todo.details.key] ||
                                todo.details.key,
                            ' ',
                            todo.details.value));
                    }
                    case TodoType.Bespoke: {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null, todo.details.name)));
                    }
                    case TodoType.TriggerAgent: {
                        return (React.createElement(React.Fragment, null,
                            React.createElement("b", null, lang.workflowTodoTypes[TodoType.TriggerAgent]),
                            props.onChange ? (React.createElement(TriggerAgentPromptEditor, { todo: todo, procedure: props.procedure, onChange: props.onChange })) : (React.createElement(TriggerAgentPrompt, null, todo.details.prompt))));
                    }
                    default: {
                        return React.createElement("b", null, lang.workflowTodoTypes[todo.type]);
                    }
                }
            })(),
            props.onChange && (React.createElement(Trash2, { style: {
                    color: THEME.COLOR_ACCENT,
                    cursor: 'pointer',
                }, onClick: () => {
                    if (props.onChange) {
                        props.onChange(props.procedure &&
                            props.procedure.filter((_, index) => index !== i));
                    }
                } }))),
        showTodoDetails && (React.createElement(Modal, { onToggle: () => {
                setShowTodoDetails(false);
            }, header: lang.workflowTodoTypes[todo.type] },
            React.createElement(TodoDetails, { todoDetails: todo.details, keyLangMap: {
                    ...lang.caseMergeRules,
                    mergeWithRelatedCases: lang.workflows.mergeWithRelatedCasesOfType,
                    caseMergeRules: lang.navigation.settingsCaseMergeRules,
                    caseStatus: lang.workflows.casesInTask,
                    templateId: lang.documents.fromTemplate,
                    includeVoucherAttachments: lang.workflows.includeVoucherAttachments,
                }, valueLangMap: {
                    ...lang.cases.caseStatus,
                    ...Object.fromEntries(Object.entries(updateTypesMap).map(([k, v]) => [k, v.label])),
                    ...Object.fromEntries(Object.entries(templatesMap).map(([k, v]) => [k, v.name])),
                    ...lang.updateTypes,
                    ...lang.customerCaseRelationType,
                }, style: { paddingLeft: 0 } })))));
};
const TodoDetails = (props) => {
    return (React.createElement(List, { style: props.style }, Object.entries(props.todoDetails).map(([detailsKey, detailsValue]) => {
        return (React.createElement(TodoDetailItem, { detailsKey: detailsKey, detailsValue: detailsValue, keyLangMap: props.keyLangMap, valueLangMap: props.valueLangMap }));
    })));
};
const TodoDetailItem = (props) => {
    const hasChildren = typeof props.detailsValue === 'object' && !!props.detailsValue;
    const isKeyANumber = !isNaN(Number(props.detailsKey));
    return (React.createElement(React.Fragment, { key: props.detailsKey },
        React.createElement("li", null,
            React.createElement("b", null, isKeyANumber
                ? ''
                : (props.keyLangMap[props.detailsKey] || props.detailsKey) + ': '),
            !hasChildren &&
                (() => {
                    if (props.detailsValue in props.valueLangMap) {
                        return props.valueLangMap[props.detailsValue];
                    }
                    if (typeof props.detailsValue === 'boolean' ||
                        !props.detailsValue) {
                        return (React.createElement(Checkbox, { disabled: true, checked: props.detailsValue || props.detailsValue === null, size: Size.Small }));
                    }
                    return props.detailsValue || '';
                })()),
        hasChildren && (React.createElement(TodoDetails, { todoDetails: props.detailsValue, keyLangMap: props.keyLangMap, valueLangMap: props.valueLangMap }))));
};
const TriggerAgentPromptEditor = (props) => {
    const lang = useSelector((s) => s.lang);
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(props.todo.details.prompt);
    const openModal = () => {
        setDraft(props.todo.details.prompt);
        setOpen(true);
    };
    const trimmed = draft.trim();
    const isOverMax = trimmed.length > TRIGGER_AGENT_PROMPT_MAX_LENGTH;
    const isValid = trimmed.length > 0 && !isOverMax;
    const isDirty = trimmed !== props.todo.details.prompt;
    const save = () => {
        if (isValid && isDirty) {
            const updated = {
                ...props.todo,
                details: { ...props.todo.details, prompt: trimmed },
            };
            props.onChange((props.procedure ?? []).map((todo) => todo.id === props.todo.id ? updated : todo));
        }
        setOpen(false);
    };
    return (React.createElement(React.Fragment, null,
        ' ',
        React.createElement(EditPromptButton, { onClick: openModal },
            React.createElement(Edit, { style: { cursor: 'pointer' } })),
        React.createElement(TriggerAgentPrompt, null, props.todo.details.prompt),
        open && (React.createElement(Modal, { header: lang.workflowTodoTypes[TodoType.TriggerAgent], onToggle: () => setOpen(false), maxWidth: "600px", footer: React.createElement("div", { style: { textAlign: 'right' } },
                React.createElement(Button, { onClick: save, disabled: !isValid || !isDirty }, lang.inputs.save)) },
            React.createElement(Textarea, { autoFocus: true, value: draft, placeholder: lang.workflows.triggerAgent.promptPlaceholder, onChange: (e) => setDraft(e.target.value), style: { minHeight: 220, marginBottom: 4 } }),
            React.createElement("div", { style: {
                    textAlign: 'right',
                    fontSize: 12,
                    color: isOverMax ? THEME.DANGEROUS : THEME.TEXT_MUTED,
                } },
                trimmed.length,
                " / ",
                TRIGGER_AGENT_PROMPT_MAX_LENGTH)))));
};
const EditPromptButton = styled.span `
  cursor: pointer;
  vertical-align: middle;
  margin-left: 4px;

  & > svg {
    width: 14px;
    height: 14px;
  }
`;
const TriggerAgentPrompt = styled.div `
  margin-top: 4px;
  margin-bottom: 4px;
  padding: 6px 8px;
  background: ${THEME.SURFACE_MUTED};
  border-radius: 3px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${THEME.TEXT_MUTED};
`;
const List = styled.ul `
  li {
    background-color: ${THEME.FILL};
    list-style: none;
    padding: 0.5em;
    margin: 0.2em 0;
    border-radius: 3px;
  }

  ul li {
    background-color: ${THEME.SURFACE_MUTED};
  }

  ul ul li {
    background-color: ${THEME.SURFACE_INSET};
  }

  ul {
    padding-left: 2em;
  }
`;
//# sourceMappingURL=Procedure.js.map